import {Component, Input, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {NgbDatepicker, NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {Data, GetAllWrappedResponse} from "../../models/getAllWrappedDataModel";
import {RemarkData} from "../../models/remarkDataModel";
import {Store} from "@ngrx/store";
import {clearRemarkData} from "../../store/createoredit/createoredit.action";
import {Select2Data, Select2Value} from "ng-select2-component";
import {MatCheckbox} from "@angular/material/checkbox";
import {CreateExceptionFormGroup} from "../../models/createExceptionFormGroupModel";
import {CreateOrEditFormGroup} from "../../models/createOrEditFormGroupModel";

type CalendarDay = { day: number, month: number, year: number, today: boolean, selectable: boolean };
type Month = { header: string, formControl: FormControl<Date[] | null | undefined>, start: Date, end: Date };
type Selected = {
  disabledDates: Date[];
  calendarMonths: Array<Month>;
  name: string;
  validTrains: GetAllWrappedResponse["data"][number]["validTrains"];
  selectableTrainNumberOptions: Select2Data;
  selectedTrainIds: Select2Value[];
  trafficData: Data | null;
  startDate: Date;
  endDate: Date;
};

@Component({
  selector: 'calendar-section',
  templateUrl: './calendar-section.component.html',
  styleUrls: ['./calendar-section.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    NgbDatepicker,
    MatCheckbox
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class CalendarSectionComponent implements OnDestroy {
  @Input() remarkData!: RemarkData | null;
  @Input() exceptionId!: string;
  @Input({ required: true }) selectedYear!: Selected;
  @Input({ required: true }) form!: CreateExceptionFormGroup | CreateOrEditFormGroup;
  constructor(
      private store: Store,
  ) {}

  ngOnDestroy(): void {
    // Needed to not flash previous page's selectedTrains
    this.store.dispatch(clearRemarkData());
  }

  getHolidayType(calendarDay: CalendarDay) {
    const holidays = this.selectedYear.trafficData!.holidays
        .map(a => ({
          type: a.type,
          date: new Date(a.date),
        }));

    return holidays.find(holiday => {
      return this.isSameDate(holiday.date, new Date(calendarDay.year, calendarDay.month, calendarDay.day));
    })?.type;
  }

  toggleAllWeekdays(checked: boolean) {
    const holidays = this.selectedYear.trafficData!.holidays
        .filter(holiday => holiday.type === 1 || holiday.type === 2)
        .map(a => new Date(a.date));

    this.selectedYear.calendarMonths.forEach(month => {
      const weekdaysInMonth = this.getAllDatesBetween(month.start, month.end)
        .filter(day => day.getDay() >= 1 && day.getDay() <= 5)
        .filter(day => !holidays.some(holiday => this.isSameDate(day, holiday)));

      if (checked) {
        month.formControl.setValue(this.mergeUniqueDates(month.formControl.value ?? [], weekdaysInMonth));
      } else {
        const monthWithoutWeekdays = month.formControl.value
          ?.filter(day => !weekdaysInMonth.some(weekday => this.isSameDate(day, weekday))) || [];
        month.formControl.setValue(monthWithoutWeekdays.length ? monthWithoutWeekdays : undefined)
      }
    });
  }

  toggleAllSaturdays(checked: boolean) {
    this.selectedYear.calendarMonths.forEach(month => {
      const saturdaysInMonth = this.getAllDatesBetween(month.start, month.end)
        .filter(day => day.getDay() === 6);

      if (checked) {
        month.formControl.setValue(this.mergeUniqueDates(month.formControl.value ?? [], saturdaysInMonth));
      } else {
        const monthWithoutSaturdays = month.formControl.value
            ?.filter(day => day.getDay() !== 6) || [];
        month.formControl.setValue(monthWithoutSaturdays.length ? monthWithoutSaturdays : undefined)
      }
    });
  }

  toggleAllSundays(checked: boolean) {
    const allHolidays = this.selectedYear.trafficData!.holidays
      .filter(holiday => holiday.type === 1 || holiday.type === 2)
      .map(holiday => new Date(holiday.date));

    this.selectedYear.calendarMonths.forEach(month => {
      const sundaysAndHolidaysInMonth = this.getAllDatesBetween(month.start, month.end)
        .filter(day => day.getDay() === 0 || allHolidays.some(holiday => this.isSameDate(day, holiday)));

      if (checked) {
        month.formControl.setValue(this.mergeUniqueDates(month.formControl.value ?? [], sundaysAndHolidaysInMonth));
      } else {
        const monthWithoutSundays = month.formControl.value
            ?.filter(day => day.getDay() !== 0 && !allHolidays.some(holiday => this.isSameDate(day, holiday))) || [];
        month.formControl.setValue(monthWithoutSundays.length ? monthWithoutSundays : undefined)
      }
    });
  }

  toggleNgbDate(month: Month, date: NgbDateStruct) {
    const selectedDate = this.fromNgbDate(date);

    if (!this.isDateInMonth(selectedDate, month) || this.isDisabledDate(selectedDate)) return;

    const selectedDates = month.formControl.value ?? [];
    const selectedDateIndex = selectedDates.findIndex(currentDate => this.isSameDate(currentDate, selectedDate));

    if (selectedDateIndex === -1) {
      month.formControl.setValue([...selectedDates, selectedDate]);
      return;
    }

    const updatedDates = selectedDates.filter(currentDate => !this.isSameDate(currentDate, selectedDate));
    month.formControl.setValue(updatedDates.length ? updatedDates : undefined);
  }

  isSameDate(dateA: Date, dateB: Date) {
    return (
        dateA.getMonth() === dateB.getMonth() &&
        dateA.getDate() === dateB.getDate() &&
        dateA.getFullYear() === dateB.getFullYear()
    )
  }

  isDateInMonth(date: Date, month: Month) {
    return date.getFullYear() === month.start.getFullYear()
      && date.getMonth() === month.start.getMonth();
  }

  isDisabledDate(date: Date) {
    return this.selectedYear.disabledDates.some(disabledDate => this.isSameDate(disabledDate, date));
  }

  toNgbDate(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  fromNgbDate(date: NgbDateStruct): Date {
    return new Date(date.year, date.month - 1, date.day);
  }

  isDateSelected(month: Month, date: NgbDateStruct) {
    const asDate = this.fromNgbDate(date);
    return month.formControl.value?.some(selectedDate => this.isSameDate(selectedDate, asDate)) ?? false;
  }

  isHolidayByType(date: NgbDateStruct, holidayType: 1 | 2) {
    const asDate = this.fromNgbDate(date);
    return this.getHolidayType({
      day: asDate.getDate(),
      month: asDate.getMonth(),
      year: asDate.getFullYear(),
      today: false,
      selectable: true,
    }) === holidayType;
  }

  toggleWholeMonthSelected(month: Month) {
    const clickedMonthIndex = this.selectedYear.calendarMonths.findIndex(a => a.start === month.start);
    if (clickedMonthIndex === -1) return;

    const allDatesInMonth = this.getAllDatesBetween(this.selectedYear.calendarMonths[clickedMonthIndex].start, this.selectedYear.calendarMonths[clickedMonthIndex].end);

    if (allDatesInMonth.length === this.selectedYear.calendarMonths[clickedMonthIndex].formControl.value?.length) {
      // All days in month is selected. Deselect whole month
      this.selectedYear.calendarMonths[clickedMonthIndex].formControl.reset();
    } else {
      // Some or none of days in month is selected. Select whole month
      this.selectedYear.calendarMonths[clickedMonthIndex].formControl.setValue(allDatesInMonth);
    }
  }

  getAllDatesBetween = (startDate: Date, endDate: Date) => {
    const dates = [];

    // Strip away hours, minutes, seconds etc.
    let currentDate = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
    );

    while (currentDate <= endDate) {
      dates.push(currentDate);
      currentDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate() + 1,
      );
    }
    return dates;
  };

  mergeUniqueDates(existingDates: Date[], datesToAdd: Date[]) {
    const mergedDates = [...existingDates];

    datesToAdd.forEach(dateToAdd => {
      const alreadyExists = mergedDates.some(existingDate => this.isSameDate(existingDate, dateToAdd));
      if (!alreadyExists) {
        mergedDates.push(dateToAdd);
      }
    });

    return mergedDates;
  }

  getMarkDisabled(month: Month) {
    return (date: NgbDateStruct) => {
      const asDate = this.fromNgbDate(date);
      const isOutsideCurrentMonth = !this.isDateInMonth(asDate, month);
      const isOutsideRange = asDate < this.selectedYear.startDate || asDate > this.selectedYear.endDate;

      return isOutsideCurrentMonth || isOutsideRange || this.isDisabledDate(asDate);
    };
  }

  getCustomDateStyle(dateToCheck: Date) {
    const isSelected = this.selectedYear.calendarMonths.some(month =>
      month.formControl.value?.some(selectedDate =>
        this.isSameDate(selectedDate, dateToCheck)
      )
    );

    if (isSelected) return 'calendar-selected';

    const holidayType = this.getHolidayType({
      day: dateToCheck.getDate(),
      month: dateToCheck.getMonth(),
      year: dateToCheck.getFullYear(),
      today: false,
      selectable: true,
    });

    switch (holidayType) {
      case 1:
        return 'highlight-eve';
      case 2:
        return 'highlight-holiday';
      default:
        return '';
    }
  }
}
